import { useEffect, useMemo, useState } from "react";
import { Check, Loader2, Search } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Input } from "@/components/ui/input";
import {
    Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { OPS } from "@/constants/testIds";

const C = OPS.wm.admin;
const LIMIT = 60;

/** Local search folding: case, spacing, and the Arabic letters staff type either way. */
const fold = (s) => String(s || "")
    .toLowerCase()
    .replace(/[أإآ]/g, "ا").replace(/ة/g, "ه").replace(/ى/g, "ي")
    .replace(/[ً-ْـ]/g, "")
    .replace(/\s+/g, " ")
    .trim();

function Option({ r, current, onPick }) {
    const name = r.recipe_name || r.recipe_key;
    return (
        <button type="button" data-testid={C.recipeOption(r.recipe_key)} onClick={() => onPick(r)}
                className="flex w-full items-center justify-between gap-2 rounded-[var(--radius-sm)] px-3 py-2.5 text-start text-sm text-[color:var(--ink)] hover:bg-[color:var(--brand-orange-soft)]">
            <span className="min-w-0">
                <bdi className="block truncate font-semibold">{name}</bdi>
                {r.recipe_name && r.recipe_key && r.recipe_key !== r.recipe_name ? (
                    <bdi className="block truncate text-[10px] text-[color:var(--ink-faint)]">{r.recipe_key}</bdi>
                ) : null}
            </span>
            {current && current === r.recipe_key ? (
                <Check className="h-4 w-4 shrink-0 text-[color:var(--brand-orange)]" />
            ) : null}
        </button>
    );
}

/**
 * Pick the POS recipe for one W&M item. The item's own suggestion and its
 * alternatives come first; under them, the server's recipe search
 * (`GET /waste-meals/admin/recipes`, names only — never a line cost). If the
 * search cannot be reached, the recipes the matcher proposed anywhere in the
 * table are searched locally instead.
 */
export function RecipePickerDialog({ open, onOpenChange, itemName, suggested, pool, current, onPick }) {
    const { t } = useI18n();
    const [q, setQ] = useState("");
    const [remote, setRemote] = useState(null);      // null = not loaded / unreachable
    const [searching, setSearching] = useState(false);

    useEffect(() => { if (open) { setQ(""); setRemote(null); } }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        let alive = true;
        const needle = q.trim();
        const h = setTimeout(async () => {
            setSearching(true);
            try {
                const r = await api.get("/waste-meals/admin/recipes", { params: { q: needle || undefined, limit: LIMIT } });
                if (alive) setRemote(Array.isArray(r.data?.rows) ? r.data.rows : []);
            } catch {
                if (alive) setRemote(null);
            } finally {
                if (alive) setSearching(false);
            }
        }, needle ? 250 : 0);
        return () => { alive = false; clearTimeout(h); };
    }, [open, q]);

    const view = useMemo(() => {
        const needle = fold(q);
        const match = (r) => !needle || fold(r.recipe_name).includes(needle) || fold(r.recipe_key).includes(needle);
        const sugKeys = new Set((suggested || []).map((r) => r.recipe_key));
        const top = (suggested || []).filter(match);
        // the server already matched on its own folded keys: do not filter its answer again
        const source = remote !== null ? remote : (pool || []).filter(match);
        const rest = source.filter((r) => r?.recipe_key && !sugKeys.has(r.recipe_key));
        return { top, rest, capped: remote !== null && remote.length >= LIMIT };
    }, [q, suggested, pool, remote]);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={C.recipePicker} className="max-w-lg gap-0 p-0">
                <DialogHeader className="px-4 pb-2 pt-4 text-start sm:text-start">
                    <DialogTitle className="text-base">{t("wm_picker_recipe_title")}</DialogTitle>
                    <DialogDescription className="text-xs">{itemName}</DialogDescription>
                </DialogHeader>
                <div className="px-4 pb-2">
                    <div className="relative">
                        <Search className="pointer-events-none absolute start-2.5 top-3 h-4 w-4 text-[color:var(--ink-faint)]" />
                        <Input data-testid={C.recipePickerSearch} value={q} autoFocus dir="auto"
                               onChange={(e) => setQ(e.target.value)} placeholder={t("wm_picker_recipe_search")}
                               className="h-10 pe-8 ps-8 text-base" />
                        {searching ? (
                            <Loader2 className="pointer-events-none absolute end-2.5 top-3 h-4 w-4 animate-spin text-[color:var(--ink-faint)]" />
                        ) : null}
                    </div>
                </div>
                <div className="max-h-[56vh] overflow-y-auto scrollbar-thin px-2 pb-3">
                    {view.top.length ? (
                        <div className="mb-1">
                            <p className="px-3 pb-1 pt-2 text-[10px] font-bold text-[color:var(--ink-faint)]">
                                {t("wm_picker_alternatives")}
                            </p>
                            {view.top.map((r) => <Option key={r.recipe_key} r={r} current={current} onPick={onPick} />)}
                        </div>
                    ) : null}
                    {view.rest.length ? (
                        <div className="mb-1">
                            <p className="px-3 pb-1 pt-2 text-[10px] font-bold text-[color:var(--ink-faint)]">
                                {t("wm_picker_other")}
                            </p>
                            {view.rest.map((r) => <Option key={r.recipe_key} r={r} current={current} onPick={onPick} />)}
                            {view.capped ? (
                                <p className="px-3 py-1.5 text-[10px] text-[color:var(--ink-faint)]">{t("wm_picker_type_more")}</p>
                            ) : null}
                        </div>
                    ) : null}
                    {!view.top.length && !view.rest.length && !searching ? (
                        <p data-testid={C.recipePickerEmpty} className="px-3 py-6 text-center text-sm text-[color:var(--ink-muted)]">
                            {t("wm_picker_recipe_empty")}
                        </p>
                    ) : null}
                </div>
            </DialogContent>
        </Dialog>
    );
}
